"use client";

import Image from "next/image";
import type { CSSProperties } from "react";
import { HIBERNATION_POKU_FILTER_STRONG } from "@/constants/arcade-styles";

interface DisabledCabinetProps {
  /** Sign held by the in-world character. */
  sign?: string;
  /** Status-bar copy at the bottom edge of the cabinet (Eileen canon). */
  status?: string;
  /** Path to the character sprite. */
  characterSrc?: string;
}

const COBALT = "#3E61FA";

/**
 * Diegetic disabled state — flickering, dying to dead register.
 *
 * Operator direction (session-3, marks #4 + #5):
 *  - "The effect here should flicker dying"
 *  - "The astronaut should hover around but oddly"
 *  - "The OUT OF ORDER banner needs to be back here too"
 *
 * Composition (operator session-4: "this announcement should be alive
 * but barely — it's an announcement of death"; later: "integrate the
 * sign into the screen, get creative, text over poku centered"). The
 * astronaut is the dead body (dying register); the announcement layers
 * are alive but barely (LIVING register on the slowest/middle variants):
 *  - Astronaut: HIBERNATION_POKU_FILTER_STRONG + .kansei-hover-oddly
 *    + .kansei-flicker-a (DYING — he is dead, drifting in zero-G)
 *  - OUT OF ORDER text: cobalt + .kansei-typewriter-out-of-order
 *    (one-shot quantum reveal, steps(14) over 2.4s) +
 *    .kansei-flicker-living-c (LIVING settle — the screen broadcasts
 *    the closure, the text emerges FROM the surface, then sits
 *    flickering). Centered horizontally + vertically over the
 *    astronaut. No more tilted plaque pinned in front of the bear.
 *  - Brick-by-brick: strong filter + .kansei-flicker-living-b (LIVING —
 *    the brand mark stays visible, struggling)
 *  - Donations bar: dim cobalt + .kansei-flicker-living-c (LIVING —
 *    the institutional copy keeps announcing)
 */
export function DisabledCabinet({
  sign = "OUT OF ORDER",
  status = "DONATIONS WELCOME · 1 HENLO = 1 MOONDUST",
  characterSrc = "/poku/astronaut.png",
}: DisabledCabinetProps = {}) {
  return (
    <div className="pointer-events-auto absolute inset-0 z-40 select-none">
      <div className="absolute inset-0 flex items-center justify-center">
        {/* Astronaut — hovers oddly + flickers dying. Centered in the
            cabinet so the OUT OF ORDER text below can overlay him at
            the same center. */}
        <div className="kansei-hover-oddly z-[5] will-change-transform">
          <div className="kansei-flicker-a will-change-[opacity]">
            <Image
              src={characterSrc}
              alt="Poku astronaut, holding the Henlo globe on the moon"
              width={240}
              height={240}
              priority={false}
              style={{
                filter: HIBERNATION_POKU_FILTER_STRONG,
              }}
            />
          </div>
        </div>
      </div>

      {/* OUT OF ORDER — emerges FROM the screen surface, types out
          tetris-style (quantum steps(14) clip reveal), holds for ~8s,
          gets hard-wiped (steps(1)), brief blackout, types again. 12s
          loop. LIVING register flicker on the parent runs alongside.
          Sits as cobalt signal directly over the dead astronaut, both
          centered in the cabinet. The previous floating tilted plaque
          was a physical object competing with the bear; this version
          reads as the screen itself broadcasting the closure. */}
      <div className="kansei-flicker-living-c pointer-events-none absolute inset-0 z-[15] flex items-center justify-center">
        <span
          className="kansei-typewriter-out-of-order-loop font-pressStart2P text-[28px] uppercase tracking-[0.18em] whitespace-nowrap"
          data-text={sign}
          style={{
            // Color + text-shadow are applied to ::after via CSS vars.
            // Setting them on the span itself causes the (transparent)
            // original text to still cast its shadow — visible as a
            // "separate dark layer" alongside ::after's animated text
            // during the typewriter reveal. Only set the CSS vars here.
            "--kansei-typewriter-color": COBALT,
            "--kansei-typewriter-shadow":
              "0 0 2px rgba(62, 97, 250, 0.45), 0 1px 0 rgba(0, 0, 0, 0.85)",
          } as CSSProperties}
        >
          {sign}
        </span>
      </div>

      <div
        className="kansei-flicker-living-b pointer-events-none absolute bottom-[40px] left-1/2 z-[7] -translate-x-1/2 will-change-[opacity]"
        style={{
          // Brand mark scaled proportionally to the bigger announcement
          // (132×36 → 180×50). Preserves the 3.66:1 ratio. Pushed down
          // slightly (bottom-34 → bottom-40) so the brick has breath
          // between it and the donations bar.
          width: 180,
          height: 50,
          opacity: 1,
          filter: HIBERNATION_POKU_FILTER_STRONG,
        }}
      >
        <Image
          src="/lobby/brickbybrick.svg"
          alt="Brick by Brick"
          fill
          className="object-contain"
        />
      </div>

      <div
        className="kansei-flicker-living-c absolute inset-x-0 bottom-0 z-[6] flex items-center justify-center gap-2 px-3 py-[5px] will-change-[opacity]"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.85) 35%, rgba(0,0,0,0.95) 100%)",
          borderTop: `1px solid rgba(62,97,250,0.22)`,
        }}
      >
        <p
          className="font-pressStart2P text-[7px] uppercase tracking-[0.22em] text-center"
          style={{
            // Light cobalt-tinted moondust at full alpha so the kansei
            // flicker (basal 0.32 → gasp 0.50) lands as legible-but-dim
            // rather than invisible. Operator (session-4): bottom copy
            // wasn't readable when alpha + flicker stacked.
            color: "rgba(155, 176, 240, 1)",
            textShadow: "0 1px 0 rgba(0, 0, 0, 0.7)",
          }}
        >
          {status}
        </p>
      </div>
    </div>
  );
}
